import { useLocation, useNavigate } from 'react-router-dom'

import { Tabs } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import useSearchParams from 'modules/core/hooks/useSearchParams'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { useTaskTypes } from 'modules/bookkeepers/lib/TaskTypesContext'
import { trackSwitchDetailTab } from 'modules/bookkeepers/lib/amplitudeData'
import { useIncidenceMessages } from 'modules/bookkeepers/store/collections/incidenceMessages'
import { useIncidenceNotes } from 'modules/bookkeepers/store/collections/incidenceNotes'
import { MESSAGE_SENDER } from 'modules/bookkeepers/store/models/Incidence'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { TABS } from 'modules/payroll_bundle/lib/constants'
import { buildTabs } from 'modules/payroll_bundle/lib/utils'

interface Props {
  selectedTab: string
  incidenceId: number
  isBookkeeper: boolean
}

const TabSelector = ({ selectedTab, incidenceId, isBookkeeper }: Props) => {
  const { incidenceType } = useSearchParams<{ incidenceType: string }>()
  const taskTypes = useTaskTypes()
  const incidence = useCurrentIncidence()
  const taskStatuses = incidence.taskStatuses
  const { navigation } = useNavigation()
  const { hasInhousePayroll } = useInhousePayroll()
  const incidenceNotes = useIncidenceNotes()
  const incidenceMessages = useIncidenceMessages()
  const { hasCompensations } = useHasCompensations()
  const navigate = useNavigate()
  const location = useLocation()
  const isDataCollection = location.pathname.includes('employee-updates')

  const handleClick = (url: string, name: string) => {
    trackSwitchDetailTab(incidence, name, isBookkeeper, hasInhousePayroll)
    navigate(url)
  }

  const totalTasks = taskTypes.length
  const completedTasks = +taskStatuses?.filter((status) => status.completed).length
  const taskSummary = totalTasks > 0 ? `(${completedTasks}/${totalTasks})` : undefined
  const totalNotes = incidenceNotes.length
  const notesSummary = totalNotes > 9 ? '(9+)' : `(${totalNotes})`
  const totalMessages = incidenceMessages.length
  const messagesSummary = totalMessages > 9 ? '(9+)' : `(${totalMessages})`
  const tabsData = buildTabs(
    incidenceId,
    isBookkeeper,
    navigation,
    incidenceType,
    taskSummary,
    hasInhousePayroll,
    hasCompensations,
    notesSummary,
    messagesSummary,
    isDataCollection
  )

  return (
    <Tabs
      onTabClicked={(tabId) => {
        const tab = tabsData.find(({ id }) => id === tabId)
        if (tab) {
          handleClick(tab.url, tab.id)
        }
      }}
      currentId={selectedTab}
      hasAdditionalPadding
    >
      {tabsData.map((tab) => {
        const hasMessage = incidence.hasMessage
        const messageForUser = isBookkeeper
          ? hasMessage && incidence.messageFrom === MESSAGE_SENDER.admin
          : hasMessage && incidence.messageFrom === MESSAGE_SENDER.bookkeeper
        const isUpdatesTab = tab.id === TABS.updates
        const isMessageTab = tab.id === TABS.messages
        const incidenceRead = !!incidence?.readAt
        const showNotification =
          ((isUpdatesTab && !incidenceRead) || (isMessageTab && messageForUser)) ?? false

        return (
          <Tabs.Tab
            key={tab.id}
            id={tab.id}
            label={tab.name}
            tag={tab.additionalInformation}
            showNotification={showNotification}
          />
        )
      })}
    </Tabs>
  )
}

export default observer(TabSelector)
